// Where to send the user after a successful login. The home page (or any gated
// entry point) stashes the intended destination under "postLoginRedirect" before
// opening the login dialog; login completion calls this to consume it. Falls back
// to /dashboard. Only same-origin, absolute-path routes are honored so a stale or
// tampered value can't turn login into an open redirect.
export function postLoginRedirect(fallback: string = "/dashboard"): string {
    const target = sessionStorage.getItem("postLoginRedirect");
    sessionStorage.removeItem("postLoginRedirect");
    if (target && target.startsWith("/") && !target.startsWith("//")) {
        return target;
    }
    return fallback;
}

/**
 * Does the page somebody is heading for actually need a persona?
 *
 * ONE RUN DOES NOT. `/reporting?run=<usid>` shows a RECORDED run, and `Get Report Runs` consults
 * the realm and who ran it -- never the caller's persona. A run's rows belong to whoever produced
 * them, deliberately: they were captured under one persona at one moment, so replaying them to a
 * different one would be wrong rather than merely narrower. The persona a reader happens to hold
 * cannot change what that page shows.
 *
 * WHICH MAKES THE PROMPT PURE FRICTION ON THAT PATH -- a question whose answer changes nothing,
 * asked of somebody who has just followed a link out of their mailbox and wants the numbers the
 * email was about. Ed, 2026-09-30: sign in, choose a persona, arrive nowhere.
 *
 * NARROW ON PURPOSE. Every other destination does need one: `/reporting` with no run is a live
 * question compiled under the reader's persona, and so is a dashboard. Only the recorded-run link
 * is exempt, and it is exempt because of a property of the endpoint rather than a preference.
 *
 * PEEKS, NEVER CONSUMES. `postLoginRedirect` is what clears the key; asking this question must not
 * spend the answer.
 */
export function destinationNeedsPersona(): boolean {
    let target: string | null = null;
    try {
        target = sessionStorage.getItem("postLoginRedirect");
    } catch {
        return true;
    }
    if (!target || !target.startsWith("/") || target.startsWith("//")) return true;
    const [path, query = ""] = target.split("?");
    return !(path === "/reporting" && new URLSearchParams(query).get("run"));
}
